import {getEventId, isPastOrCanceled} from '@wix/wix-events-commons-statics'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {isMobile} from '../../../../../commons/selectors/environment'
import {useMembersPageActions, useMembersPageSelector} from '../../hooks/runtime'
import {hasOrder, isExpanded, isTicketedWithNoOrder} from '../../selectors/event'
import {getEventList, getEventsTotal, hasNoEvents} from '../../selectors/events'
import {getExpandedEvents} from '../../selectors/expanded-events'
import {isLoading} from '../../selectors/loading'
import {getCurrentTab} from '../../selectors/tab'
import {isOwnProfile} from '../../selectors/user'
import {DetailedEvent} from '../../types/state'
import {CancelRsvpButton} from '../actions/cancel-rsvp-button'
import {DetailsButton} from '../actions/details-button'
import {DownloadTicketsButton} from '../actions/download-tickets-button'
import {GoToOrderButton} from '../actions/go-to-order-button'
import {MobileDownloadTicketsButton} from '../actions/mobile-download-tickets-button'
import {ShareButton} from '../actions/share-button'
import {EventDetails} from '../event-details'
import {EventSummary} from '../event-summary'
import {ExpandableRow} from '../expandable-row'
import {NoEvents} from '../no-events'
import {EventLink} from '../styled-components/event-link'
import s from './events-list.scss'
import {ActionProps} from './interfaces'

export const EventsList = () => {
  const {t} = useTranslation()

  const mobile = useMembersPageSelector(isMobile)
  const noEvents = useMembersPageSelector(hasNoEvents)
  const events = useMembersPageSelector(getEventList)
  const total = useMembersPageSelector(getEventsTotal)
  const hasMore = events.length < total
  const expandedEvents = useMembersPageSelector(getExpandedEvents)
  const loading = useMembersPageSelector(isLoading)
  const owner = useMembersPageSelector(isOwnProfile)
  const tab = useMembersPageSelector(getCurrentTab)

  const {closeAllEvents, toggleEventDetails, getEvents} = useMembersPageActions()

  const handleToggle = (event: DetailedEvent) => {
    if (getEventId(event.event) === expandedEvents[0]) {
      closeAllEvents()
    } else {
      closeAllEvents()
      toggleEventDetails({event})
    }
  }

  const handleLoadMore = () => {
    if (!loading) {
      getEvents({tab})
    }
  }

  if (noEvents && !loading) {
    return <NoEvents />
  }

  return (
    <div className={mobile ? s.mobile : null} role="list">
      {events.map(event => (
        <ExpandableRow
          key={getEventId(event.event)}
          isExpanded={isExpanded(expandedEvents, event)}
          toggle={() => handleToggle(event)}
          arrowClass={s.arrow}
          mobile={mobile}
          t={t}
          details={
            <EventDetails
              event={event}
              primaryButton={<DetailsButton event={event} />}
              secondaryButton={<Action mobile={mobile} event={event} isOwner={owner} />}
              shareButton={<ShareButton event={event} />}
            />
          }
          role="listitem"
        >
          <EventSummary event={event} />
        </ExpandableRow>
      ))}
      {hasMore && (
        <div className={classNames(s.loadMore, {[s.mobileLoadMore]: mobile})}>
          <EventLink secondary mobile={mobile} onClick={handleLoadMore} loading={loading}>
            {t('events.buttons.load-more')}
          </EventLink>
        </div>
      )}
    </div>
  )
}

const Action = ({isOwner, mobile, event}: ActionProps) => {
  if (!isOwner) {
    return null
  }

  if (isTicketedWithNoOrder(event)) {
    return mobile ? <MobileDownloadTicketsButton event={event} /> : <DownloadTicketsButton event={event} />
  }

  if (hasOrder(event)) {
    if (mobile) {
      return <GoToOrderButton event={event} />
    } else {
      return null
    }
  }

  if (isPastOrCanceled(event.event)) {
    return null
  }

  return <CancelRsvpButton event={event} />
}
